'use client';

/**
 * AppNavBar Component (Lean)
 *
 * Unified navigation bar — lean version with just search + actions.
 * Page title, description, and badges are now rendered in-content
 * by PageContentHeader inside PageTemplate.
 *
 * Layout:
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │ [☰]     │        [Search]        │  [AI] [Notif] [User]  │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * - Left: Menu button (mobile only)
 * - Center: Search/CommandPalette (flex: 1)
 * - Right: Actions + User menu
 *
 * @see Issue #330 - Unified Navigation System
 * @see PR #856 — Navigation & Layout Redesign
 */

import AccountCircle from '@mui/icons-material/AccountCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DarkModeIcon from '@mui/icons-material/Brightness4';
import LightModeIcon from '@mui/icons-material/Brightness7';
import CheckIcon from '@mui/icons-material/Check';
import Logout from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MidnightIcon from '@mui/icons-material/NightsStay';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { memo, type ReactNode, useState } from 'react';
import { useI18n } from '../../hooks';
import { RAIL_WIDTH } from '../../hooks/useSidebarRail';
import type { ThemeMode } from '../../theme';
import { iconSizes } from '../../theme/typography';
import { computeUserInitials } from '../../utils/user-initials';
import { GlassTooltip } from '../GlassTooltip';

export interface AppNavBarUser {
  name?: string;
  email?: string;
  avatarUrl?: string;
}

/** Height of the AppNavBar toolbar in pixels — design pass-4 spec
 * (shell.jsx TopBar: `height: 48`). Was 44 prior to PR feat/v3-design-pass-4. */
export const HEADER_HEIGHT = 48;

export interface AppNavBarProps {
  // ============================================================================
  // Branding
  // ============================================================================
  /** Portal/business name shown in left branding area */
  /**
   * Optional custom brand renderer. When provided, completely replaces the
   * default logo + portalName cluster on desktop AND mobile. Use this to
   * render a portal-specific `BrandBlock` (e.g. the v3 OpsMatrix mint mark +
   * caption + workspace switcher) without forking the entire AppNavBar.
   *
   * `onLogoClick` still fires on the wrapping Box when the slot is present,
   * so legacy click-the-logo-to-go-home behavior is preserved.
   */
  brandSlot?: ReactNode;
  portalName?: string;
  /** Logo URL (image path). When provided, renders an img instead of the default platform icon. */
  logoUrl?: string;
  /** Handler for logo/branding click (typically navigates to dashboard) */
  onLogoClick?: () => void;

  // ============================================================================
  // Page Header (deprecated — now rendered in-content by PageContentHeader)
  // ============================================================================
  /** @deprecated Page title is now rendered in-content by PageContentHeader */
  pageTitle?: string;
  /** @deprecated Page icon is now rendered in-content by PageContentHeader */
  pageIcon?: React.ComponentType<SvgIconProps>;
  /** @deprecated Page description is now rendered in-content by PageContentHeader */
  pageDescription?: string;
  /** @deprecated Page badges are now rendered in-content by PageContentHeader */
  pageBadges?: ReactNode;
  /** @deprecated Page actions are now rendered in-content by PageContentHeader */
  pageActions?: ReactNode;
  /** @deprecated Back handler is now in PageContentHeader */
  onBack?: () => void;

  // ============================================================================
  // Navigation Controls
  // ============================================================================
  /** Show hamburger menu button */
  showMenuButton?: boolean;
  /** Handler for menu button click */
  onMenuClick?: () => void;
  /** Custom breadcrumbs component (always shown below toolbar) */
  breadcrumbs?: ReactNode;

  // ============================================================================
  // Search
  // ============================================================================
  /** Show search area */
  showSearch?: boolean;
  /** Custom search component (e.g., CommandPalette SearchButton) */
  searchComponent?: ReactNode;

  // ============================================================================
  // Theme
  // ============================================================================
  /** Show theme toggle/selector */
  showThemeToggle?: boolean;
  /** Current theme mode */
  themeMode?: ThemeMode;
  /** Handler for theme mode change */
  onThemeModeChange?: (mode: ThemeMode) => void;
  /** Available theme modes for dropdown */
  availableModes?: ThemeMode[];
  /** Function to get label for a theme mode */
  getModeLabel?: (mode: ThemeMode) => string;
  /** Use simple toggle instead of dropdown (dark/light only) */
  simpleThemeToggle?: boolean;
  /** Handler for simple toggle */
  onThemeToggle?: () => void;

  // ============================================================================
  // User
  // ============================================================================
  /** Current user info */
  user?: AppNavBarUser;
  /** Show user email next to avatar */
  showUserEmail?: boolean;
  /** Handler for logout */
  onLogout?: () => void;
  /** Handler for profile navigation */
  onProfile?: () => void;
  /** Handler for settings navigation */
  onSettings?: () => void;
  /**
   * Handler for "Notification preferences" menu item. When provided, an extra
   * item appears in the profile dropdown between Settings and Logout — useful
   * for portals where notification prefs live on a dedicated page rather than
   * inside generic Settings.
   */
  onNotificationPreferences?: () => void;
  /**
   * Handler for "Astor preferences" menu item. When provided, an extra item
   * appears in the profile dropdown linking to the user's Astor preferences
   * tab (auto-fill, draft replies, surface refi opps, etc.).
   */
  onAstorPreferences?: () => void;

  // ============================================================================
  // Layout
  // ============================================================================
  /** Width of sidebar (used for branding area width) */
  sidebarWidth?: number;
  /** AppBar position */
  position?: 'fixed' | 'sticky';
  /** Additional actions to render in the right section */
  actions?: ReactNode;
  /** Elevation level */
  elevation?: number;
  /** Hide user avatar on desktop (when avatar is shown in sidebar rail instead) */
  hideUserAvatar?: boolean;
}

/**
 * Get icon for a theme mode
 */
function getThemeIcon(themeMode: ThemeMode) {
  switch (themeMode) {
    case 'midnight':
      return <MidnightIcon fontSize="small" />;
    case 'dark':
      return <DarkModeIcon fontSize="small" />;
    case 'light':
    default:
      return <LightModeIcon fontSize="small" />;
  }
}

/**
 * Default theme mode labels
 */
function defaultGetModeLabel(mode: ThemeMode): string {
  switch (mode) {
    case 'midnight':
      return 'Midnight';
    case 'dark':
      return 'Dark';
    case 'light':
    default:
      return 'Light';
  }
}

function AppNavBarImpl({
  // Branding
  brandSlot,
  portalName,
  logoUrl,
  onLogoClick,

  // Deprecated page header props (ignored in lean mode)
  pageTitle: _pageTitle,
  pageIcon: _pageIcon,
  pageDescription: _pageDescription,
  pageBadges: _pageBadges,
  pageActions: _pageActions,
  onBack: _onBack,

  // Navigation
  showMenuButton = true,
  onMenuClick,
  breadcrumbs,

  // Search
  showSearch = true,
  searchComponent,

  // Theme
  showThemeToggle = true,
  themeMode = 'dark',
  onThemeModeChange,
  availableModes = ['light', 'dark', 'midnight'],
  getModeLabel = defaultGetModeLabel,
  simpleThemeToggle = false,
  onThemeToggle,

  // User
  user,
  showUserEmail: _showUserEmail,
  onLogout,
  onProfile,
  onSettings,
  onNotificationPreferences,
  onAstorPreferences,

  // Layout
  sidebarWidth = 240,
  position = 'sticky',
  actions,
  elevation: _elevation = 1,
  hideUserAvatar = false,
}: AppNavBarProps) {
  const { t } = useI18n();
  const muiTheme = useMuiTheme();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // User menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleThemeSelect = (selectedMode: ThemeMode) => {
    onThemeModeChange?.(selectedMode);
    handleUserMenuClose();
  };

  // User action handlers
  const handleLogout = () => {
    handleUserMenuClose();
    onLogout?.();
  };

  const handleProfile = () => {
    handleUserMenuClose();
    onProfile?.();
  };

  const handleSettings = () => {
    handleUserMenuClose();
    onSettings?.();
  };

  const handleNotificationPreferences = () => {
    handleUserMenuClose();
    onNotificationPreferences?.();
  };

  const handleAstorPreferences = () => {
    handleUserMenuClose();
    onAstorPreferences?.();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Main Navigation Bar */}
      <AppBar
        position={position}
        color="default"
        elevation={0}
        sx={{
          // Unified glass background (matches SidebarRail and SidebarPanel)
          bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(18, 18, 20, 0.92)' : 'rgba(245, 245, 248, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          // shell.jsx TopBar: `borderBottom: 1px solid F.border` — adds the
          // crisp horizontal rule that separates the topbar from page chrome.
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          boxShadow: 'none',
          // Full-width: spans entire viewport including over the sidebar
          ...(position === 'fixed' && {
            width: '100%',
            ml: 0,
            zIndex: muiTheme.zIndex.drawer + 1,
          }),
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${String(HEADER_HEIGHT)}px !important`,
            px: { xs: 2, md: 0 },
            display: 'flex',
            gap: 0,
          }}
        >
          {/* Left Section - Hamburger + Logo/Branding area */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              width: Math.max(sidebarWidth, 200),
              minWidth: 200,
              flexShrink: 0,
              gap: 0,
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease',
            }}
          >
            {/* Desktop hamburger — centered within RAIL_WIDTH to align with sidebar rail icons */}
            {showMenuButton && onMenuClick && (
              <Box
                sx={{
                  width: RAIL_WIDTH,
                  minWidth: RAIL_WIDTH,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label={t('a11y.toggleSidebar')}
                  onClick={onMenuClick}
                  sx={{ color: 'text.secondary', width: 44, height: 44 }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {/* Logo / brand slot */}
            {brandSlot ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: onLogoClick ? 'pointer' : 'default',
                  '&:hover': onLogoClick ? { opacity: 0.85 } : {},
                  px: showMenuButton ? 0 : 1,
                }}
                onClick={onLogoClick}
              >
                {brandSlot}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: onLogoClick ? 'pointer' : 'default',
                  '&:hover': onLogoClick ? { opacity: 0.8 } : {},
                  px: showMenuButton ? 0 : 2,
                }}
                onClick={onLogoClick}
              >
                <Box
                  component="img"
                  src={logoUrl ?? (themeMode === 'light' ? '/fiops-mark-light.svg' : '/fiops-mark-dark.svg')}
                  alt={logoUrl ? portalName || 'Logo' : 'Logo'}
                  sx={{ height: 28, width: 28, flexShrink: 0, borderRadius: '6px' }}
                />
                {portalName && (
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      color: 'text.primary',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {portalName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Mobile: Menu button + branding */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              px: 0,
            }}
          >
            {showMenuButton && onMenuClick && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label={t('a11y.openMenu')}
                onClick={onMenuClick}
                sx={{ color: 'text.secondary', width: 44, height: 44 }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Center Section - Search (fills available space) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flex: 1,
              justifyContent: 'center',
              px: 3,
            }}
          >
            {showSearch && searchComponent}
          </Box>

          {/* Mobile spacer (when search is hidden, push right section to end) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flex: 1 }} />

          {/* Right Section - Actions + User Menu
              shell.jsx TopBar uses `gap: 6` between the right cluster's buttons. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.75,
              flexShrink: 0,
              pr: { xs: 0, md: 1.75 },
            }}
          >
            {/* Global Actions (AI, Notifications, etc.) */}
            {actions}

            {/* User Menu (hidden on desktop when avatar is in sidebar rail) */}
            {user && (
              <Box sx={hideUserAvatar ? { display: { xs: 'flex', md: 'none' } } : {}}>
                <GlassTooltip title={user.name || t('common.user')}>
                  <span>
                    {/* IconButton padding (8px) + 28px avatar = 44px hit area
                        (Apple HIG floor / portals.md §11b touch-target rule);
                        visual circle remains compact per shell.jsx (26px in
                        the design — we use 28px so the mono initials read
                        cleanly without truncation, which is within the design's
                        visual cluster). */}
                    <IconButton onClick={handleUserMenuOpen} sx={{ p: 1 }} aria-label={t('a11y.openUserMenu')}>
                      <Avatar
                        src={user.avatarUrl}
                        sx={{
                          width: 28,
                          height: 28,
                          // shell.jsx avatar uses `F.panel3` (a subtle elevated
                          // panel tone) rather than the brand primary — keeps
                          // the cluster visually quiet. Maps to the theme's
                          // hover/secondary surface tone.
                          bgcolor: 'action.selected',
                          color: 'text.primary',
                          fontSize: iconSizes.xs,
                          fontWeight: 600,
                          fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                          border: `1px solid ${muiTheme.palette.divider}`,
                        }}
                      >
                        {user.name ? computeUserInitials(user.name) : 'U'}
                      </Avatar>
                    </IconButton>
                  </span>
                </GlassTooltip>

                {/* User Menu Dropdown */}
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  slotProps={{
                    paper: { sx: { minWidth: 240, mt: 1 } },
                  }}
                >
                  {/* User Info Header */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user.name || t('common.user')}
                    </Typography>
                    {user.email && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        {user.email}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  {/* Theme Selector (inside dropdown) - Using flattened array for MUI Menu compatibility */}
                  {showThemeToggle && [
                    <Box key="theme-label" sx={{ px: 2, py: 1 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 600,
                          letterSpacing: 1,
                        }}
                      >
                        {t('theme.label')}
                      </Typography>
                    </Box>,
                    ...(simpleThemeToggle
                      ? [
                          <MenuItem
                            key="theme-toggle"
                            onClick={() => {
                              onThemeToggle?.();
                              handleUserMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              {themeMode === 'dark' ? (
                                <LightModeIcon fontSize="small" />
                              ) : (
                                <DarkModeIcon fontSize="small" />
                              )}
                            </ListItemIcon>
                            {themeMode === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
                          </MenuItem>,
                        ]
                      : availableModes.map((mode) => (
                          <MenuItem
                            key={mode}
                            onClick={() => handleThemeSelect(mode)}
                            selected={themeMode === mode}
                            sx={{
                              py: 1,
                              '&.Mui-selected': {
                                bgcolor: alpha(muiTheme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>{getThemeIcon(mode)}</ListItemIcon>
                            <Typography variant="body2">{getModeLabel(mode)}</Typography>
                            {themeMode === mode && <CheckIcon fontSize="small" color="primary" sx={{ ml: 'auto' }} />}
                          </MenuItem>
                        ))),
                    <Divider key="theme-divider" sx={{ my: 1 }} />,
                  ]}

                  {onProfile && (
                    <MenuItem onClick={handleProfile}>
                      <ListItemIcon>
                        <AccountCircle fontSize="small" />
                      </ListItemIcon>
                      {t('navigation.profile')}
                    </MenuItem>
                  )}

                  {onSettings && (
                    <MenuItem onClick={handleSettings}>
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      {t('navigation.settings')}
                    </MenuItem>
                  )}

                  {onNotificationPreferences && (
                    <MenuItem onClick={handleNotificationPreferences}>
                      <ListItemIcon>
                        <NotificationsIcon fontSize="small" />
                      </ListItemIcon>
                      {t('navigation.notificationPreferences')}
                    </MenuItem>
                  )}

                  {onAstorPreferences && (
                    <MenuItem onClick={handleAstorPreferences}>
                      <ListItemIcon>
                        <AutoAwesomeIcon fontSize="small" />
                      </ListItemIcon>
                      {t('navigation.astorPreferences')}
                    </MenuItem>
                  )}

                  {(onProfile || onSettings || onNotificationPreferences || onAstorPreferences) && onLogout && (
                    <Divider />
                  )}

                  {onLogout && (
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <Logout fontSize="small" color="error" />
                      </ListItemIcon>
                      <Typography color="error">{t('auth.logout')}</Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {/* Breadcrumbs (always below, no background) */}
      {breadcrumbs && (
        <Box
          sx={{
            px: 3,
            py: 1,
          }}
        >
          {breadcrumbs}
        </Box>
      )}
    </Box>
  );
}

/**
 * Memoized: AppNavBar receives many props (portalName, callbacks, breadcrumbs, etc.).
 * When parent re-renders for context churn, this bails out if references are stable.
 * Portals MUST `useCallback` the event handlers and `useMemo` object props, else the
 * memo is a no-op.
 */
export const AppNavBar = memo(AppNavBarImpl);

export default AppNavBar;
